/* Phase 8A — Soft route guard. Redirects hidden-module URLs to the user's
 * first-visible module and shows a subtle toast.
 *
 * Used only inside the authenticated shell — public routes (login, portal)
 * bypass it because they don't share the shell.
 *
 * While the visibility matrix is still loading and the current route is a
 * gated module, render a light loader instead of the child. This prevents
 * a brief mount of e.g. SalesPipeline (which would fire its own API call)
 * before the guard can redirect.
 */
import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useModuleVisibility } from "@/store/moduleVisibility";
import { useMatrixAny } from "@/hooks/usePermission";
import { ROUTE_MODULE_MAP } from "@/lib/nav";

function routeModuleKey(pathname) {
  // Longest-prefix match so `/admin/rules` and `/admin/audit` still resolve to `admin`.
  const hit = [...ROUTE_MODULE_MAP]
    .sort((a, b) => b.prefix.length - a.prefix.length)
    .find((r) => pathname === r.prefix || pathname.startsWith(r.prefix + "/"));
  return hit?.key || null;
}

// D31 -- explicit per-route escape hatches. Routes listed here bypass
// the module_visibility check when the matched matrix keys resolve to
// True. Used to give HR_MANAGER / MANAGER access to the grant-request
// filing UI at /admin/rules/user-permissions without opening the whole
// Admin module.
const MATRIX_ROUTE_EXCEPTIONS = [
  { pathPrefix: "/admin/rules/user-permissions",
    keys: ["user_permission_overrides_view", "grant_request_create"] },
];

function pathHasMatrixEscape(pathname, matrix) {
  for (const ex of MATRIX_ROUTE_EXCEPTIONS) {
    if (pathname === ex.pathPrefix || pathname.startsWith(ex.pathPrefix + "/")) {
      if (ex.keys.some((k) => !!matrix?.[k])) return true;
    }
  }
  return false;
}

export default function ModuleGuard({ children }) {
  const { t } = useTranslation();
  const loc = useLocation();
  const { modules, loaded, firstVisiblePath } = useModuleVisibility();
  // D31 -- matrix escape checks (see MATRIX_ROUTE_EXCEPTIONS above).
  const matrixEscape = useMatrixAny(
    "user_permission_overrides_view", "grant_request_create",
  );
  const key = routeModuleKey(loc.pathname);

  const moduleHidden = loaded && modules && key && modules[key] === false;
  // D31 -- suppress the redirect if the current path has a matrix-key
  // escape hatch AND the user has one of those keys.
  const escapeActive = moduleHidden && matrixEscape
    && pathHasMatrixEscape(loc.pathname, { user_permission_overrides_view: matrixEscape,
                                             grant_request_create: matrixEscape });
  const hidden = moduleHidden && !escapeActive;
  const target = hidden ? firstVisiblePath("/") : null;

  useEffect(() => {
    if (hidden) {
      try { toast(t("modules.notAvailable")); } catch (_) { /* toast optional */ }
    }
  }, [hidden, t]);

  if (hidden) return <Navigate to={target} replace />;
  // Matrix not loaded yet AND we're on a gated route → show loader.
  if (!loaded && key) {
    return (
      <div className="flex items-center justify-center py-24 text-foreground/50 text-xs gap-2"
           data-testid="module-guard-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  return children;
}
